import React, { useState, useEffect, useRef, useCallback } from 'react';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { toast } from 'sonner';
import { 
  MessageCircle, Send, Search, User, Truck, Store, RefreshCw, Smile,
  MoreVertical, Ticket, Package, X, Filter, ChevronDown, Paperclip,
  File, FileText, Image as ImageIcon, Check, CheckCheck, Loader2
} from 'lucide-react';
import Picker from 'emoji-picker-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
  DropdownMenuLabel
} from '@/components/ui/dropdown-menu';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList
} from '@/components/ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';

export default function AdminChat() {
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [availableUsers, setAvailableUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [showUserSelector, setShowUserSelector] = useState(false);
  const [userFilter, setUserFilter] = useState('all'); // all, vendor, driver
  const [searchQuery, setSearchQuery] = useState('');
  const [showTicketDialog, setShowTicketDialog] = useState(false);
  const [ticketTitle, setTicketTitle] = useState('');
  
  // New states for enhancements
  const [isTyping, setIsTyping] = useState(false);
  const [otherUserTyping, setOtherUserTyping] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [smartReplies, setSmartReplies] = useState([]);
  const [loadingReplies, setLoadingReplies] = useState(false);
  
  const messagesEndRef = useRef(null);
  const pollIntervalRef = useRef(null);
  const messageInputRef = useRef(null);
  const fileInputRef = useRef(null);
  const typingTimeoutRef = useRef(null);

  // All useEffect hooks must be at the top level
  useEffect(() => {
    fetchConversations();
    fetchAvailableUsers();
    
    // Real-time polling every 3 seconds
    pollIntervalRef.current = setInterval(() => {
      fetchConversations();
      if (selectedConversation) {
        fetchMessages(selectedConversation.conversation_id, true);
      }
    }, 3000);
    
    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
      }
    };
  }, [selectedConversation]);

  // Separate effect to handle URL parameter when users are loaded
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const userId = params.get('user');
    
    if (userId && availableUsers.length > 0 && !selectedConversation) {
      const user = availableUsers.find(u => u.id === userId);
      if (user) {
        handleStartNewChat(user);
        // Clear URL parameter after opening chat
        window.history.replaceState({}, '', '/admin/chat');
      }
    }
  }, [availableUsers]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Poll for typing indicators
  useEffect(() => {
    if (!selectedConversation?.conversation_id) return;
    
    const interval = setInterval(async () => {
      try {
        const res = await api.get(`/admin/chat/typing/${selectedConversation.conversation_id}`);
        setOtherUserTyping(res.data.is_typing);
      } catch (err) {
        // Silently fail
      }
    }, 1000);
    
    return () => clearInterval(interval);
  }, [selectedConversation]);

  // Handle file selection
  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'application/pdf'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Only images (JPEG, PNG, WebP) and PDFs are allowed');
      return;
    }
    
    // Validate file size (10MB)
    const maxSize = 10 * 1024 * 1024; // 10MB
    if (file.size > maxSize) {
      toast.error('File too large. Maximum size is 10MB');
      return;
    }
    
    setSelectedFile(file);
    toast.success(`File selected: ${file.name}`);
  };

  // Remove selected file
  const handleRemoveFile = () => {
    setSelectedFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Convert file to base64
  const fileToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => {
        // Remove data URL prefix to get pure base64
        const base64 = reader.result.split(',')[1];
        resolve(base64);
      };
      reader.onerror = (error) => reject(error);
    });
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const fetchConversations = async () => {
    try {
      const res = await api.get('/admin/chat/conversations');
      setConversations(res.data.conversations || []);
    } catch (err) {
      console.error('Failed to fetch conversations:', err);
    }
  };

  const fetchAvailableUsers = async () => {
    try {
      const res = await api.get('/admin/chat/users');
      setAvailableUsers(res.data.users || []);
    } catch (err) {
      console.error('Failed to fetch users:', err);
    }
  };

  const fetchMessages = async (conversationId, silent = false) => {
    if (!silent) setLoading(true);
    try {
      const res = await api.get(`/admin/chat/messages/${conversationId}`);
      setMessages(res.data.messages || []);
    } catch (err) {
      if (!silent) toast.error('Failed to load messages');
    } finally {
      if (!silent) setLoading(false);
    }
  };
  
  // Define handleTyping outside of useEffect
  const handleTypingDebounce = useCallback(() => {
    if (!selectedConversation?.conversation_id) return;
    
    // Clear previous timeout
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }
    
    // Set typing indicator
    if (!isTyping) {
      setIsTyping(true);
      api.post('/admin/chat/typing', {
        conversation_id: selectedConversation.conversation_id,
        is_typing: true
      }).catch(() => {});
    }
    
    // Clear typing after 3 seconds of inactivity
    typingTimeoutRef.current = setTimeout(() => {
      setIsTyping(false);
      api.post('/admin/chat/typing', {
        conversation_id: selectedConversation.conversation_id,
        is_typing: false
      }).catch(() => {});
    }, 3000);
  }, [selectedConversation, isTyping]);

  const handleSelectConversation = async (conv) => {
    setSelectedConversation(conv);
    setShowUserSelector(false);
    setShowEmojiPicker(false);
    fetchMessages(conv.conversation_id);
    
    // Fetch AI smart replies
    fetchSmartReplies(conv.conversation_id);
  };

  const fetchSmartReplies = async (conversationId) => {
    if (!conversationId) return;
    
    setLoadingReplies(true);
    try {
      const res = await api.get(`/admin/chat/smart-replies/${conversationId}`);
      setSmartReplies(res.data.suggestions || []);
    } catch (err) {
      console.error('Failed to fetch smart replies:', err);
      setSmartReplies([]);
    } finally {
      setLoadingReplies(false);
    }
  };

  const handleSmartReplyClick = (suggestion) => {
    setNewMessage(suggestion);
    setSmartReplies([]); // Hide suggestions after selection
    messageInputRef.current?.focus();
  };

  const handleStartNewChat = async (user) => {
    const existing = conversations.find(c => c.participant.id === user.id);
    if (existing) {
      handleSelectConversation(existing);
      return;
    }

    setSelectedConversation({
      conversation_id: null,
      participant: user,
      last_message: null,
      unread_count: 0
    });
    setMessages([]);
    setShowUserSelector(false);
  };

  const handleSendMessage = async () => {
    if ((!newMessage.trim() && !selectedFile) || !selectedConversation) return;

    setSending(true);
    const tempMessage = newMessage;
    const tempFile = selectedFile;
    setNewMessage(''); // Clear immediately for better UX
    setSelectedFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    
    try {
      let attachment_data = null;
      let attachment_name = null;
      let attachment_type = null;
      
      // Process file attachment if present
      if (tempFile) {
        setUploadingFile(true);
        attachment_data = await fileToBase64(tempFile);
        attachment_name = tempFile.name;
        attachment_type = tempFile.type;
      }
      
      const res = await api.post('/admin/chat/send', {
        recipient_id: selectedConversation.participant.id,
        message: tempMessage || (tempFile ? `Sent ${tempFile.name}` : ''),
        conversation_id: selectedConversation.conversation_id,
        attachment_data,
        attachment_name,
        attachment_type
      });

      // Update messages with the new message
      setMessages(prevMessages => [...prevMessages, res.data.message_data]);
      
      // If this was a new conversation, update the conversation ID
      if (!selectedConversation.conversation_id) {
        setSelectedConversation(prev => ({
          ...prev,
          conversation_id: res.data.conversation_id
        }));
      }
      
      // Refresh conversations list to update sidebar
      fetchConversations();
      
      // Scroll to bottom to show new message
      setTimeout(scrollToBottom, 100);
      messageInputRef.current?.focus();
      
      if (tempFile) {
        toast.success('File sent successfully');
      }
    } catch (err) {
      toast.error('Failed to send message');
      setNewMessage(tempMessage); // Restore message on error
      setSelectedFile(tempFile);
    } finally {
      setSending(false);
      setUploadingFile(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleEmojiClick = (emojiData) => {
    setNewMessage(prev => prev + emojiData.emoji);
    setShowEmojiPicker(false);
    messageInputRef.current?.focus();
  };

  const handleConvertToTicket = async () => {
    if (!ticketTitle.trim() || !selectedConversation) return;

    try {
      // Get conversation messages
      const conversationText = messages
        .map(m => `[${m.sender_role === 'admin' ? 'Admin' : m.sender_name}]: ${m.message}`)
        .join('\n\n');

      await api.post('/admin/tickets', {
        subject: ticketTitle,
        description: `Converted from chat with ${selectedConversation.participant.name}\n\n--- Chat History ---\n${conversationText}`,
        category: 'general',
        priority: 'medium',
        is_internal: false,
        created_by_admin: true,
        created_by_name: selectedConversation.participant.name,
        created_by_email: selectedConversation.participant.email
      });

      toast.success('Chat converted to ticket successfully');
      setShowTicketDialog(false);
      setTicketTitle('');
    } catch (err) {
      toast.error('Failed to convert chat to ticket');
    }
  };

  const handleQuickAction = async (action) => {
    if (action === 'assign_order') {
      toast.info('Order assignment coming soon');
    } else if (action === 'view_orders') {
      window.location.href = `/admin/orders?search=${selectedConversation.participant.name}`;
    } else if (action === 'view_profile') {
      const role = selectedConversation.participant.role;
      if (role === 'vendor') {
        window.location.href = `/admin/vendors`;
      } else if (role === 'delivery_worker') {
        window.location.href = `/admin/drivers`;
      }
    }
  };

  const getRoleIcon = (role) => {
    if (role === 'vendor') return <Store className="h-4 w-4" />;
    if (role === 'delivery_worker') return <Truck className="h-4 w-4" />;
    return <User className="h-4 w-4" />;
  };

  const getRoleBadgeColor = (role) => {
    if (role === 'vendor') return 'bg-emerald-100 text-emerald-700 border-emerald-200';
    if (role === 'delivery_worker') return 'bg-blue-100 text-blue-700 border-blue-200';
    return 'bg-gray-100 text-gray-700 border-gray-200';
  };

  const filteredUsers = availableUsers.filter(user => {
    const matchesSearch = 
      user.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      user.email.toLowerCase().includes(searchQuery.toLowerCase());
    
    if (userFilter === 'vendor') return matchesSearch && user.role === 'vendor';
    if (userFilter === 'driver') return matchesSearch && user.role === 'delivery_worker';
    return matchesSearch;
  });

  const formatTime = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffMins < 1440) return `${Math.floor(diffMins / 60)}h ago`;
    return date.toLocaleDateString();
  };

  return (
    <div className="fixed inset-0 top-16 flex bg-gradient-to-br from-slate-50 to-slate-100 dark:from-slate-900 dark:to-slate-800" data-testid="admin-chat-page">
      {/* Left Panel - Conversations */}
      <div className="w-96 border-r border-border/50 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/80 flex flex-col shadow-lg h-full">
        <div className="p-4 border-b border-border/50">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-[#9A031E] to-[#7a0218] flex items-center justify-center shadow-lg">
                <MessageCircle className="h-5 w-5 text-white" />
              </div>
              <div>
                <h2 className="text-lg font-bold text-foreground">Messages</h2>
                <p className="text-xs text-muted-foreground">
                  {conversations.length} conversation{conversations.length !== 1 ? 's' : ''}
                </p>
              </div>
            </div>
            <Button size="sm" variant="ghost" onClick={fetchConversations} className="h-9 w-9 p-0">
              <RefreshCw className="h-4 w-4" />
            </Button>
          </div>

          {/* New Chat Selector */}
          <Popover open={showUserSelector} onOpenChange={setShowUserSelector}>
            <PopoverTrigger asChild>
              <Button variant="outline" className="w-full justify-start text-left font-normal border-dashed">
                <MessageCircle className="mr-2 h-4 w-4" />
                Start New Chat
                <ChevronDown className="ml-auto h-4 w-4 opacity-50" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-80 p-0" align="start">
              <Command>
                <CommandInput placeholder="Search vendors or drivers..." onValueChange={setSearchQuery} />
                <div className="flex gap-1 p-2 border-b">
                  <Button
                    size="sm"
                    variant={userFilter === 'all' ? 'default' : 'outline'}
                    onClick={() => setUserFilter('all')}
                    className="flex-1"
                  >
                    All
                  </Button>
                  <Button
                    size="sm"
                    variant={userFilter === 'vendor' ? 'default' : 'outline'}
                    onClick={() => setUserFilter('vendor')}
                    className="flex-1"
                  >
                    <Store className="h-3 w-3 mr-1" />
                    Vendors
                  </Button>
                  <Button
                    size="sm"
                    variant={userFilter === 'driver' ? 'default' : 'outline'}
                    onClick={() => setUserFilter('driver')}
                    className="flex-1"
                  >
                    <Truck className="h-3 w-3 mr-1" />
                    Drivers
                  </Button>
                </div>
                <CommandList>
                  <CommandEmpty>No users found.</CommandEmpty>
                  <CommandGroup heading="Available Users">
                    {filteredUsers.slice(0, 20).map((user) => (
                      <CommandItem
                        key={user.id}
                        onSelect={() => handleStartNewChat(user)}
                        className="flex items-center gap-3 py-3"
                      >
                        <Avatar className="h-8 w-8">
                          <AvatarFallback className={getRoleBadgeColor(user.role)}>
                            {getRoleIcon(user.role)}
                          </AvatarFallback>
                        </Avatar>
                        <div className="flex-1 min-w-0">
                          <div className="font-medium text-sm truncate">{user.name}</div>
                          <div className="text-xs text-muted-foreground truncate">{user.email}</div>
                        </div>
                        <Badge variant="outline" className={`text-xs ${getRoleBadgeColor(user.role)}`}>
                          {user.role === 'delivery_worker' ? 'Driver' : 'Vendor'}
                        </Badge>
                      </CommandItem>
                    ))}
                  </CommandGroup>
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>
        </div>

        {/* Conversations List */}
        <ScrollArea className="flex-1 px-2">
          {conversations.length === 0 ? (
            <div className="text-center py-12 px-4">
              <MessageCircle className="h-12 w-12 mx-auto mb-3 text-muted-foreground/30" />
              <p className="text-sm text-muted-foreground">No conversations yet</p>
              <p className="text-xs text-muted-foreground mt-1">Start chatting with vendors or drivers</p>
            </div>
          ) : (
            <div className="space-y-1 py-2">
              {conversations.map((conv) => (
                <div
                  key={conv.conversation_id}
                  onClick={() => handleSelectConversation(conv)}
                  className={`group relative p-3 rounded-lg cursor-pointer transition-all duration-200 ${
                    selectedConversation?.conversation_id === conv.conversation_id
                      ? 'bg-[#9A031E]/10 border border-[#9A031E]/20 shadow-sm'
                      : 'hover:bg-accent/50 border border-transparent'
                  }`}
                  data-testid="conversation-item"
                >
                  <div className="flex items-start gap-3">
                    <div className="relative">
                      <Avatar className="h-11 w-11 border-2 border-background shadow-sm">
                        <AvatarFallback className={getRoleBadgeColor(conv.participant.role)}>
                          {getRoleIcon(conv.participant.role)}
                        </AvatarFallback>
                      </Avatar>
                      {conv.participant.is_online && (
                        <div className="absolute -bottom-0.5 -right-0.5 h-3 w-3 bg-green-500 rounded-full border-2 border-background"></div>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-2 mb-1">
                        <span className="font-semibold text-sm truncate">{conv.participant.name}</span>
                        {conv.unread_count > 0 && (
                          <Badge variant="destructive" className="h-5 px-1.5 text-xs font-bold">
                            {conv.unread_count}
                          </Badge>
                        )}
                      </div>
                      <div className="flex items-center gap-2 mb-1">
                        <Badge variant="outline" className={`text-xs ${getRoleBadgeColor(conv.participant.role)}`}>
                          {getRoleIcon(conv.participant.role)}
                          <span className="ml-1">{conv.participant.role === 'delivery_worker' ? 'Driver' : 'Vendor'}</span>
                        </Badge>
                        <span className="text-xs text-muted-foreground">{formatTime(conv.last_message.created_at)}</span>
                      </div>
                      <p className="text-xs text-muted-foreground truncate">
                        {conv.last_message.is_from_me && <span className="text-[#9A031E] font-medium">You: </span>}
                        {conv.last_message.message}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </ScrollArea>
      </div>

      {/* Right Panel - Chat */}
      <div className="flex-1 flex flex-col bg-background">
        {selectedConversation ? (
          <>
            {/* Chat Header */}
            <div className="p-4 border-b border-border/50 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/80 shadow-sm">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Avatar className="h-10 w-10 border-2 border-background shadow-sm">
                    <AvatarFallback className={getRoleBadgeColor(selectedConversation.participant.role)}>
                      {getRoleIcon(selectedConversation.participant.role)}
                    </AvatarFallback>
                  </Avatar>
                  <div>
                    <div className="font-semibold text-base">{selectedConversation.participant.name}</div>
                    <div className="flex items-center gap-2 mt-0.5">
                      <Badge variant="outline" className={`text-xs ${getRoleBadgeColor(selectedConversation.participant.role)}`}>
                        {selectedConversation.participant.role === 'delivery_worker' ? 'Driver' : 'Vendor'}
                      </Badge>
                      <span className="text-xs text-muted-foreground">{selectedConversation.participant.email}</span>
                    </div>
                  </div>
                </div>

                {/* Action Menu */}
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="sm">
                      <MoreVertical className="h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-56">
                    <DropdownMenuLabel>Quick Actions</DropdownMenuLabel>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={() => setShowTicketDialog(true)}>
                      <Ticket className="mr-2 h-4 w-4" />
                      Convert to Ticket
                    </DropdownMenuItem>
                    {selectedConversation.participant.role === 'delivery_worker' && (
                      <DropdownMenuItem onClick={() => handleQuickAction('assign_order')}>
                        <Package className="mr-2 h-4 w-4" />
                        Assign Order
                      </DropdownMenuItem>
                    )}
                    <DropdownMenuItem onClick={() => handleQuickAction('view_orders')}>
                      <Package className="mr-2 h-4 w-4" />
                      View Orders
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => handleQuickAction('view_profile')}>
                      <User className="mr-2 h-4 w-4" />
                      View Profile
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </div>

            {/* Messages */}
            <ScrollArea className="flex-1 p-4 bg-gradient-to-b from-slate-50/50 to-transparent dark:from-slate-900/50">
              {loading ? (
                <div className="flex items-center justify-center h-full">
                  <div className="text-center">
                    <RefreshCw className="h-8 w-8 animate-spin mx-auto mb-2 text-muted-foreground" />
                    <p className="text-sm text-muted-foreground">Loading messages...</p>
                  </div>
                </div>
              ) : messages.length === 0 ? (
                <div className="flex items-center justify-center h-full">
                  <div className="text-center max-w-sm">
                    <MessageCircle className="h-16 w-16 mx-auto mb-4 text-muted-foreground/30" />
                    <p className="text-base font-medium mb-1">No messages yet</p>
                    <p className="text-sm text-muted-foreground">
                      Start the conversation with {selectedConversation.participant.name}
                    </p>
                  </div>
                </div>
              ) : (
                <div className="space-y-4 max-w-4xl mx-auto">
                  {messages.map((msg, index) => {
                    const isFromMe = msg.sender_role === 'admin';
                    const showAvatar = index === 0 || messages[index - 1].sender_id !== msg.sender_id;
                    
                    return (
                      <div
                        key={msg.id}
                        className={`flex gap-3 ${isFromMe ? 'flex-row-reverse' : 'flex-row'}`}
                        data-testid="chat-message"
                      >
                        <div className={`flex-shrink-0 ${showAvatar ? 'visible' : 'invisible'}`}>
                          <Avatar className="h-8 w-8 shadow-sm">
                            <AvatarFallback className={isFromMe ? 'bg-[#9A031E] text-white' : getRoleBadgeColor(msg.sender_role || 'user')}>
                              {isFromMe ? 'A' : getRoleIcon(msg.sender_role)}
                            </AvatarFallback>
                          </Avatar>
                        </div>
                        <div className={`flex flex-col ${isFromMe ? 'items-end' : 'items-start'} max-w-[70%]`}>
                          <div
                            className={`rounded-2xl px-4 py-2.5 shadow-sm ${
                              isFromMe
                                ? 'bg-gradient-to-br from-[#9A031E] to-[#7a0218] text-white rounded-tr-sm'
                                : 'bg-white dark:bg-slate-800 text-foreground border border-border/50 rounded-tl-sm'
                            }`}
                          >
                            {msg.message && <p className="text-sm whitespace-pre-wrap break-words leading-relaxed">{msg.message}</p>}
                            
                            {/* Attachment Display */}
                            {msg.attachment_id && (
                              <div className="mt-2 pt-2 border-t border-white/20">
                                {msg.attachment_type?.startsWith('image/') ? (
                                  <a 
                                    href={`${import.meta.env.REACT_APP_BACKEND_URL || process.env.REACT_APP_BACKEND_URL}/api/admin/chat/attachment/${msg.attachment_id}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="block hover:opacity-80 transition-opacity"
                                  >
                                    <img 
                                      src={`${import.meta.env.REACT_APP_BACKEND_URL || process.env.REACT_APP_BACKEND_URL}/api/admin/chat/attachment/${msg.attachment_id}`}
                                      alt={msg.attachment_name}
                                      className="max-w-xs rounded-lg"
                                    />
                                  </a>
                                ) : (
                                  <a
                                    href={`${import.meta.env.REACT_APP_BACKEND_URL || process.env.REACT_APP_BACKEND_URL}/api/admin/chat/attachment/${msg.attachment_id}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="flex items-center gap-2 text-sm hover:underline"
                                  >
                                    <FileText className="h-4 w-4" />
                                    <span>{msg.attachment_name}</span>
                                  </a>
                                )}
                              </div>
                            )}
                          </div>
                          <div className={`flex items-center gap-1 mt-1 px-1 ${isFromMe ? 'flex-row-reverse' : 'flex-row'}`}>
                            <p className="text-xs text-muted-foreground">{formatTime(msg.created_at)}</p>
                            {/* Read Receipts for sent messages */}
                            {isFromMe && (
                              <span className="text-xs">
                                {msg.read ? (
                                  <CheckCheck className="h-3 w-3 text-blue-500" title="Read" />
                                ) : (
                                  <Check className="h-3 w-3 text-muted-foreground" title="Sent" />
                                )}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                  
                  {/* Typing Indicator */}
                  {otherUserTyping && (
                    <div className="flex gap-3">
                      <Avatar className="h-8 w-8 shadow-sm">
                        <AvatarFallback className={getRoleBadgeColor(selectedConversation.participant.role)}>
                          {getRoleIcon(selectedConversation.participant.role)}
                        </AvatarFallback>
                      </Avatar>
                      <div className="bg-white dark:bg-slate-800 rounded-2xl rounded-tl-sm px-4 py-2.5 shadow-sm border border-border/50">
                        <div className="flex gap-1">
                          <span className="w-2 h-2 bg-muted-foreground rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></span>
                          <span className="w-2 h-2 bg-muted-foreground rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></span>
                          <span className="w-2 h-2 bg-muted-foreground rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></span>
                        </div>
                      </div>
                    </div>
                  )}
                  
                  <div ref={messagesEndRef} />
                </div>
              )}
            </ScrollArea>

            {/* Message Input */}
            <div className="flex-shrink-0 p-4 border-t border-border/50 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/80">
              <div className="max-w-4xl mx-auto">
                {/* AI Smart Replies */}
                {smartReplies.length > 0 && (
                  <div className="mb-3 flex items-center gap-2 animate-in fade-in slide-in-from-bottom-2 duration-300">
                    <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                      <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                      </svg>
                      <span className="font-medium">Smart Replies:</span>
                    </div>
                    <div className="flex gap-2 flex-wrap">
                      {smartReplies.map((reply, index) => (
                        <button
                          key={index}
                          onClick={() => handleSmartReplyClick(reply)}
                          className="px-3 py-1.5 text-xs font-medium rounded-full border-2 border-[#9A031E]/20 bg-gradient-to-r from-[#9A031E]/5 to-[#9A031E]/10 hover:from-[#9A031E]/10 hover:to-[#9A031E]/20 hover:border-[#9A031E]/40 text-foreground transition-all duration-200 hover:scale-105 active:scale-95 shadow-sm hover:shadow-md"
                          data-testid={`smart-reply-${index}`}
                        >
                          {reply}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
                
                {/* File Preview */}
                {selectedFile && (
                  <div className="mb-2 flex items-center gap-2 p-2 bg-muted rounded-lg">
                    <div className="flex items-center gap-2 flex-1">
                      {selectedFile.type.startsWith('image/') ? (
                        <ImageIcon className="h-4 w-4 text-blue-500" />
                      ) : (
                        <FileText className="h-4 w-4 text-red-500" />
                      )}
                      <span className="text-sm truncate">{selectedFile.name}</span>
                      <Badge variant="outline" className="text-xs">
                        {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
                      </Badge>
                    </div>
                    <Button
                      size="icon"
                      variant="ghost"
                      onClick={handleRemoveFile}
                      className="h-6 w-6"
                    >
                      <X className="h-3 w-3" />
                    </Button>
                  </div>
                )}
                
                <div className="flex gap-2 items-end">
                  {/* File Upload Button */}
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*,.pdf"
                    onChange={handleFileSelect}
                    className="hidden"
                  />
                  <Button
                    size="icon"
                    variant="outline"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={sending || uploadingFile}
                    className="h-11 w-11 flex-shrink-0"
                    title="Attach file"
                  >
                    <Paperclip className="h-4 w-4" />
                  </Button>
                  
                  <div className="flex-1 relative">
                    <Textarea
                      ref={messageInputRef}
                      placeholder="Type your message..."
                      value={newMessage}
                      onChange={(e) => {
                        setNewMessage(e.target.value);
                        handleTypingDebounce();
                      }}
                      onKeyPress={handleKeyPress}
                      disabled={sending || uploadingFile}
                      className="min-h-[44px] max-h-32 resize-none pr-10 rounded-xl border-2 focus:border-[#9A031E]/30 transition-colors"
                      rows={1}
                      data-testid="message-input"
                    />
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => setShowEmojiPicker(!showEmojiPicker)}
                      className="absolute bottom-2 right-2 h-8 w-8 p-0 hover:bg-accent"
                      disabled={sending || uploadingFile}
                    >
                      <Smile className="h-4 w-4" />
                    </Button>
                    
                    {showEmojiPicker && (
                      <div className="absolute bottom-14 right-0 z-50">
                        <Picker onEmojiClick={handleEmojiClick} width={350} height={400} />
                      </div>
                    )}
                  </div>
                  
                  <Button 
                    onClick={handleSendMessage} 
                    disabled={(!newMessage.trim() && !selectedFile) || sending || uploadingFile}
                    className="h-11 px-6 bg-gradient-to-r from-[#9A031E] to-[#7a0218] hover:from-[#7a0218] hover:to-[#5a0112] shadow-lg"
                    data-testid="send-button"
                  >
                    {uploadingFile ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Send className="h-4 w-4" />
                    )}
                  </Button>
                </div>
              </div>
            </div>
          </>
        ) : (
          <div className="flex-1 flex items-center justify-center bg-gradient-to-br from-slate-50/50 to-slate-100/50 dark:from-slate-900/50 dark:to-slate-800/50">
            <div className="text-center max-w-md">
              <div className="h-24 w-24 mx-auto mb-6 rounded-full bg-gradient-to-br from-[#9A031E]/10 to-[#7a0218]/10 flex items-center justify-center">
                <MessageCircle className="h-12 w-12 text-[#9A031E]" />
              </div>
              <h3 className="text-2xl font-bold mb-2">Select a conversation</h3>
              <p className="text-muted-foreground mb-6">
                Choose a conversation from the list or start a new chat with vendors and drivers
              </p>
              <Button onClick={() => setShowUserSelector(true)} className="bg-gradient-to-r from-[#9A031E] to-[#7a0218]">
                <MessageCircle className="mr-2 h-4 w-4" />
                Start New Chat
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Convert to Ticket Dialog */}
      <Dialog open={showTicketDialog} onOpenChange={setShowTicketDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Convert Chat to Ticket</DialogTitle>
            <DialogDescription>
              This will create a new support ticket with the chat history included
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">Ticket Title *</label>
              <Input
                placeholder="Enter ticket title..."
                value={ticketTitle}
                onChange={(e) => setTicketTitle(e.target.value)}
              />
            </div>
            <div className="text-xs text-muted-foreground">
              The entire chat conversation will be included in the ticket description.
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowTicketDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleConvertToTicket} disabled={!ticketTitle.trim()}>
              <Ticket className="mr-2 h-4 w-4" />
              Create Ticket
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
